import { ReactElement, useEffect } from 'react'
import { useQuery, NetworkStatus } from '@apollo/client'
import { isEmpty } from 'lodash'
import { FormattedMessage, defineMessages, useIntl } from 'react-intl'
import { NavLink, useLocation } from 'react-router-dom'

import EmptyState from 'components/v2/EmptyState'
import { Loader } from '@10xLabs/react-web/components/Loader'
import Heading from 'components/v2/Heading'
import Card from '@10xLabs/react-web/components/Card'
import { Button } from 'components/v2/Button'
import { Content, ContentHeader } from 'components/v2/Content'
import { ForbiddenView } from 'features/App/components/ForbiddenView'
import { withLastUrl } from 'features/App/components/WithLastUrl'
import { FEATURES } from 'features/Roles/components/Permissions/constants'
import { canRead, canWrite } from 'features/Users/utils'
import { getQueries } from 'utils/route'
import RewardsList from './List'
import { RewardsPagination } from './Pagination'
import { useLogin } from 'features/Login/hooks'
import { GET_REWARDS } from 'features/Loyalty/queries-legacy'
import { TRewardsQueryParams } from './types'
import { GTLoyaltyRewardsResponse } from 'gql/types.generated'

const messages = defineMessages({
  helmet: {
    id: 'companies.helmet',
    defaultMessage: 'Loyalty | Rewards | Nexbus Admin'
  }
})

const LIMIT = 30

const LoyaltyRewardsView = (): ReactElement => {
  let content = <></>
  const intl = useIntl()
  const location = useLocation()
  const queries = getQueries(location.search) as TRewardsQueryParams
  const page = parseInt(queries.page ?? '1')

  const login = useLogin()
  const showNewRewardButton = canWrite(FEATURES.LOYALTY_REWARD, login.accessToken.policies)

  const { loading, error, data, refetch, networkStatus } = useQuery<{ loyaltyRewards: GTLoyaltyRewardsResponse }>(
    GET_REWARDS,
    {
      fetchPolicy: 'no-cache',
      variables: {
        pagination: {
          size: LIMIT,
          page: page - 1
        }
      }
    }
  )
  useEffect(() => {
    document.title = intl.formatMessage(messages.helmet)
  }, [])

  if (!canRead(FEATURES.LOYALTY_REWARD, login.accessToken.policies)) {
    return <ForbiddenView feature={FEATURES.LOYALTY_REWARD} />
  }

  if (networkStatus === NetworkStatus.refetch || loading) {
    content = (
      <EmptyState.Container>
        <Loader />
      </EmptyState.Container>
    )
  } else if (error) {
    content = (
      <EmptyState.Container>
        <EmptyState.Description>
          <FormattedMessage
            id="loyalty.rewards.error.description"
            defaultMessage="There was an error while loading the rewards"
          />
        </EmptyState.Description>

        <EmptyState.Description>
          <Button
            onClick={(): void => {
              refetch()
            }}
            kind="primary"
          >
            <FormattedMessage id="loyalty.rewards.error.action" defaultMessage="Reload rewards" />
          </Button>
        </EmptyState.Description>
      </EmptyState.Container>
    )
  } else if (!data?.loyaltyRewards || isEmpty(data?.loyaltyRewards.items)) {
    content = (
      <EmptyState.Container>
        <EmptyState.Description>
          <FormattedMessage
            id="loyalty.rewards.empty.description"
            defaultMessage="You don&#39;t have any rewards yet"
          />
        </EmptyState.Description>

        <EmptyState.Action>
          <Button as={NavLink} to="new" kind="primary">
            <FormattedMessage id="loyalty.rewards.empty.action" defaultMessage="Add your first reward" />
          </Button>
        </EmptyState.Action>
      </EmptyState.Container>
    )
  } else {
    content = (
      <>
        <RewardsList rewards={data.loyaltyRewards} />
        <RewardsPagination
          page={page}
          limit={LIMIT}
          totalItems={data.loyaltyRewards.total}
          currentItemsCount={data.loyaltyRewards.items.length}
        />
      </>
    )
  }

  return (
    <Content>
      <ContentHeader>
        <Heading.H3>
          <FormattedMessage id="loyalty.rewards.header.label" defaultMessage="Rewards" />
        </Heading.H3>

        {showNewRewardButton ? (
          <Button as={NavLink} to="/loyalty/rewards/new" kind="primary">
            <FormattedMessage id="loyalty.rewards.header.new" defaultMessage="+ Add new reward" />
          </Button>
        ) : null}
      </ContentHeader>

      <section>
        <Card.Container>
          <Card.Content noHeader noFooter>
            {content}
          </Card.Content>
        </Card.Container>
      </section>
    </Content>
  )
}

export default withLastUrl(LoyaltyRewardsView)
